import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import MJLogo from '../components/MJLogo';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

// Company email domain
const COMPANY_DOMAIN = "mjenterprises.net";

export default function Login() {
  const [show2FA, setShow2FA] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [setup2FACode, setSetup2FACode] = useState('');
  const [formData, setFormData] = useState({
    username: '',  // Only the part before @
    password: ''
  });
  const [resetData, setResetData] = useState({
    username: '',  // Only the part before @
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const passwordInputRef = useRef(null);

  // Handle username input (without domain)
  const handleUsernameChange = (e) => {
    // Remove any @ or domain if user tries to type it
    const value = e.target.value.replace(/@.*$/, '').replace(/\s/g, '');
    setFormData({ ...formData, username: value });
  };

  // Handle reset username input
  const handleResetUsernameChange = (e) => {
    const value = e.target.value.replace(/@.*$/, '').replace(/\s/g, '');
    setResetData({ ...resetData, username: value });
  };

  // Get full email from username (always lowercase for case-insensitive matching)
  const getFullEmail = (username) => `${username.toLowerCase()}@${COMPANY_DOMAIN}`;

  const handleForgotPasswordCheck = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/check`, {
        email: getFullEmail(resetData.username)
      });
      setForgotPasswordStep(2);
      toast.success('Please enter your 2FA code to verify identity');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to find account');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (resetData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/reset`, {
        email: getFullEmail(resetData.username),
        code: resetData.code,
        new_password: resetData.newPassword
      });
      toast.success('Password reset successfully! Please login.');
      setShowForgotPassword(false);
      setForgotPasswordStep(1);
      setResetData({ username: '', code: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: getFullEmail(formData.username),
        password: formData.password
      });

      if (response.data.requires_2fa) {
        setTempToken(response.data.temp_token);
        setShow2FA(true);
        toast.info('Please enter your 6-digit code from Google Authenticator');
      } else if (response.data.requires_2fa_setup) {
        // New user needs to set up 2FA
        setQrCode(response.data.qr_code);
        setTempToken(response.data.temp_token);
        setShowSetup2FA(true);
        toast.info('Please set up 2FA by scanning the QR code');
      } else {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Login successful!');
        // Force scroll to top before navigation (iOS fix)
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        temp_token: tempToken,
        code: twoFACode
      });

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');
      // Force scroll to top before navigation (iOS fix)
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
      setTwoFACode('');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FAComplete = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/complete-2fa-setup`, {
        temp_token: tempToken,
        code: setup2FACode
      });

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('2FA setup complete! Welcome to M&J Enterprises.');
      // Force scroll to top before navigation (iOS fix)
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
      setSetup2FACode('');
    } finally {
      setLoading(false);
    }
  };

  // 2FA Setup UI (for new users)
  if (showSetup2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <Card className="w-full max-w-md p-8 space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Set Up Two-Factor Authentication
            </h2>
            <p className="text-sm text-slate-600">
              Scan the QR code with Google Authenticator to secure your account
            </p>
          </div>

          <div className="flex justify-center bg-white p-4 rounded-lg border-2 border-slate-200">
            <img src={qrCode} alt="QR Code" className="w-48 h-48" />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900 font-medium mb-2">Steps:</p>
            <ol className="text-sm text-amber-800 space-y-1 list-decimal list-inside">
              <li>Install Google Authenticator on your phone</li>
              <li>Open the app and tap the + button</li>
              <li>Scan this QR code</li>
              <li>Enter the 6-digit code below</li>
            </ol>
          </div>

          <form onSubmit={handleSetup2FAComplete} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="setup-code">6-Digit Code from App</Label>
              <Input
                id="setup-code"
                data-testid="setup-2fa-code-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={setup2FACode}
                onChange={(e) => setSetup2FACode(e.target.value.replace(/\D/g, ''))}
                required
                className="h-12 text-center text-2xl tracking-widest"
                autoFocus
              />
            </div>

            <Button
              data-testid="complete-setup-button"
              type="submit"
              className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white"
              disabled={loading || setup2FACode.length !== 6}
            >
              {loading ? 'Verifying...' : 'Complete Setup & Login'}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => {
              setShowSetup2FA(false);
              setSetup2FACode('');
              setQrCode('');
            }}
            className="w-full text-sm text-slate-600 hover:text-slate-900"
          >
            Back to login
          </button>
        </Card>
      </div>
    );
  }

  // Forgot Password UI
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <Card className="w-full max-w-md p-8 space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {forgotPasswordStep === 1 ? 'Forgot Password' : 'Reset Password'}
            </h2>
            <p className="text-sm text-slate-600">
              {forgotPasswordStep === 1 
                ? 'Enter your email to reset your password' 
                : 'Verify with 2FA and set new password'}
            </p>
          </div>

          {forgotPasswordStep === 1 ? (
            <form onSubmit={handleForgotPasswordCheck} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="reset-username">Email</Label>
                <div className="flex items-center">
                  <Input
                    id="reset-username"
                    data-testid="reset-email-input"
                    type="text"
                    placeholder="username"
                    value={resetData.username}
                    onChange={handleResetUsernameChange}
                    required
                    className="h-10 rounded-r-none border-r-0 focus:z-10"
                    autoFocus
                  />
                  <div className="h-10 px-3 flex items-center bg-slate-100 border border-l-0 border-slate-200 rounded-r-md text-slate-500 text-sm whitespace-nowrap">
                    @{COMPANY_DOMAIN}
                  </div>
                </div>
              </div>
              <Button
                data-testid="check-email-button"
                type="submit"
                className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white"
                disabled={loading || !resetData.username}
              >
                {loading ? 'Checking...' : 'Continue'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-code">2FA Code from Google Authenticator</Label>
                <Input
                  id="reset-code"
                  data-testid="reset-2fa-code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={resetData.code}
                  onChange={(e) => setResetData({ ...resetData, code: e.target.value.replace(/\D/g, '') })}
                  required
                  className="h-12 text-center text-2xl tracking-widest"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  data-testid="new-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={resetData.newPassword}
                  onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                  required
                  className="h-10"
                />
                <PasswordStrengthIndicator password={resetData.newPassword} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  data-testid="confirm-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={resetData.confirmPassword}
                  onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                  required
                  className="h-10"
                />
              </div>
              <Button
                data-testid="reset-password-button"
                type="submit"
                className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white"
                disabled={loading || resetData.code.length !== 6}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          <button
            type="button"
            onClick={() => {
              setShowForgotPassword(false);
              setForgotPasswordStep(1);
              setResetData({ username: '', code: '', newPassword: '', confirmPassword: '' });
            }}
            className="w-full text-sm text-slate-600 hover:text-slate-900"
          >
            Back to login
          </button>
        </Card>
      </div>
    );
  }

  // 2FA Verification UI
  if (show2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <Card className="w-full max-w-md p-8 space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Enter 2FA Code
            </h2>
            <p className="text-sm text-slate-600">
              Open Google Authenticator and enter the 6-digit code
            </p>
          </div>

          <form onSubmit={handleVerify2FA} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="code">6-Digit Code</Label>
              <Input
                id="code"
                data-testid="2fa-code-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                required
                className="h-12 text-center text-2xl tracking-widest"
                autoFocus
              />
            </div>

            <Button
              data-testid="verify-2fa-button"
              type="submit"
              className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white"
              disabled={loading || twoFACode.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </Button>

            <button
              type="button"
              onClick={() => {
                setShow2FA(false);
                setTwoFACode('');
              }}
              className="w-full text-sm text-slate-600 hover:text-slate-900"
            >
              Back to login
            </button>
          </form>
        </Card>
      </div>
    );
  }

  // Main Login UI (no registration option)
  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 items-center justify-center p-12">
        <img
          src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=compress&cs=tinysrgb&w=1920"
          alt="Industrial foundry factory"
          className="w-full h-full object-cover rounded-xl"
        />
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center flex flex-col items-center">
            <MJLogo size={80} className="mb-4" />
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">
              M&J Enterprises
            </h1>
            <p className="text-sm text-amber-600 font-medium italic mb-2">We do it anyway!</p>
            <p className="text-base text-slate-600">
              Employee Portal
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6" data-testid="auth-form">
            <div className="space-y-2">
              <Label htmlFor="username">Email</Label>
              <div className="flex items-center">
                <Input
                  id="username"
                  data-testid="login-email"
                  type="text"
                  placeholder="username"
                  value={formData.username}
                  onChange={handleUsernameChange}
                  required
                  className="h-10 rounded-r-none border-r-0 focus:z-10"
                />
                <div className="h-10 px-3 flex items-center bg-slate-100 border border-l-0 border-slate-200 rounded-r-md text-slate-500 text-sm whitespace-nowrap">
                  @{COMPANY_DOMAIN}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-10"
                ref={passwordInputRef}
              />
            </div>

            <Button
              data-testid="login-submit"
              type="submit"
              className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white transition-colors"
              disabled={loading || !formData.username}
            >
              {loading ? 'Please wait...' : 'Sign In'}
            </Button>
          </form>

          <div className="text-center">
            <button
              data-testid="forgot-password-link"
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-teal-600 hover:text-teal-800 transition-colors"
            >
              Forgot your password?
            </button>
          </div>

          <div className="bg-slate-100 rounded-lg p-4 text-center">
            <p className="text-sm text-slate-600">
              <span className="font-medium">New employee?</span> Contact your manager or HR 
              to get your login credentials.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
